import { Alert, Box, Button, Card, CardContent, Container, Skeleton, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useCallback, useState, useEffect } from "react";
import { PatientDetailScreenQuery, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import SchedulerPatientBrief from "../scheduler/PatientBrief";
import VisitCard from "./VisitCard";
import VisitTimeline from "./VisitTimeline";
import { useNavigate } from "react-router-dom";
import CancelVisitDialog from "../command/CancelVisitDialog";
import CreateOverrideVisit from "~/components/scheduler/CreateOverrideVisit";
import { Permission } from "~/hooks/permissions";
import PermissionOrHide from "../common/PermissionOrHide";
import PatientCareTeamList from "./PatientCareTeamList";
import { LocalVisit } from "~/hooks/visitDragDropReducer";
import { keyBy, partition } from "lodash";
import { useAdmissionCoordination } from "~/hooks/useAdmissionCoordination";
import { AdmissionCoordinationDialog } from "~/components/demo/admission/AdmissionCoordinationDialog";
import { getAdmissionCoordination } from "~/services/demo/demoStateManager";
import { HomeHealthCalendarButton, HomeHealthPatientSection } from "~/features/home-health-demo";

type Props = {
  patient: PatientDetailScreenQuery["patientSingle"] | undefined;
  visits: PatientDetailScreenQuery["visit"];
  todaysVisits: LocalVisit[],
  loading: boolean;
  refetch: () => void;
}

const PatientDetailScreen: React.FC<Props> = ({ patient, todaysVisits, visits, loading, refetch }) => {
  const navigate = useNavigate();

  const now = new Date();
  const byId = keyBy(todaysVisits, "id");
  const updatedVisits = visits?.map(v => byId[v.id] ? { ...v, ...byId[v.id], __typename: undefined } : v) || [];
  const [pastVisits, upcomingVisits] = partition(updatedVisits, visit => new Date(visit.startTime) < now);

//   console.log(todaysVisits, visits, pastVisits, upcomingVisits)

  const [visitIdToBeEdited, setVisitIdToBeEdited] = useState<number | null>(null);
  const [visitIdToBeCanceled, setVisitIdToBeCanceled] = useState<number | null>(null);
  const [visitPopup, setVisitPopup] = useState<{ visitId: number | null; anchorEl: HTMLElement | null }>({
    visitId: null,
    anchorEl: null,
  });
  const handleCloseVisit = useCallback(() => {
    setVisitPopup({ visitId: null, anchorEl: null });
  }, []);

  // Admission coordination
  const admissionCoordination = useAdmissionCoordination();

  // Check if this patient has pending admission coordination
  // Poll every second to detect changes in localStorage
  const [admissionCoord, setAdmissionCoord] = useState(getAdmissionCoordination());

  useEffect(() => {
    const interval = setInterval(() => {
      setAdmissionCoord(getAdmissionCoordination());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const hasPendingAdmission = !!(
    patient &&
    admissionCoord &&
    admissionCoord.needsCoordination &&
    !admissionCoord.completed &&
    admissionCoord.patientId === patient.id
  );

  const handleCoordinateAdmission = () => {
    if (!patient) return;

    const activeEpisode = patient.episodes?.find((e) => e.active);
    if (!activeEpisode) return;

    const mrn = patient.patientIdentifiers?.find((pi) => pi.identifier_type === 'mrn')?.identifier || '';

    admissionCoordination.openDialog({
      id: patient.id,
      firstName: patient.firstName,
      lastName: patient.lastName,
      mrn: mrn,
      episodeId: activeEpisode.id,
      diagnosis: 'Admission',
    });
  };

  const notFound = !loading && !patient?.id;

  const continuityProviders = patient?.careTeamProviders
    .map(ctp => ({
      ...ctp, 
      lastVisit: pastVisits
        .filter(pv => pv.eventStatus == VisitEventStatusEnum.Complete && pv.visitResources.some(pvvr => pvvr.providerId == ctp.providerId))
        .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())?.[0]
    }))

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {notFound && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Patient not found
        </Alert>
      )}

      {visitIdToBeCanceled && <CancelVisitDialog
        visitId={visitIdToBeCanceled}
        onClose={() => setVisitIdToBeCanceled(null)}
        onSuccess={() => {
          setVisitIdToBeCanceled(null);
          refetch();
        }}
      />}

      {visitIdToBeEdited && (
        <CreateOverrideVisit
          key={visitIdToBeEdited}
          handleClose={() => setVisitIdToBeEdited(null)}
          calendarDateTime={new Date()}
          onSuccess={() => {
            setVisitIdToBeEdited(null);
            refetch();
          }}
          visitId={visitIdToBeEdited}
        />
      )}

      <Grid container spacing={3} style={{ marginBottom: 40 }}>
        <Grid size={12}>
          <SchedulerPatientBrief patient={patient} loading={loading} />
        </Grid>
      </Grid>

      <HomeHealthPatientSection patientId={patient?.id} patientName={patient ?? undefined} />

      <Grid container marginBottom={1}>
        <Grid size={6}>
          <Typography paddingLeft={1} fontWeight={500} component="span">
            Visit timeline
          </Typography>
          <HomeHealthCalendarButton patientId={patient?.id} patientName={patient ?? undefined} />
        </Grid>
        <Grid display='flex' size={6} justifyContent="flex-start">
          <Stack alignItems='flex-start' justifyContent='space-between' direction="row" width='100%'>
            <Typography paddingLeft={2} fontWeight={500} component="span">
              Upcoming Visits
              <HomeHealthCalendarButton patientId={patient?.id} patientName={patient ?? undefined} />
            </Typography>
            {!loading && (
              <Stack direction="row" spacing={1}>
                <PermissionOrHide permission={Permission.scheduleVisits}>
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/scheduler/patient/${patient?.id}`)}
                  >
                    Schedule New
                  </Button>
                </PermissionOrHide>
                {hasPendingAdmission && (
                  <PermissionOrHide permission={Permission.managePatients}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCoordinateAdmission}
                    >
                      Coordinate Care
                    </Button>
                  </PermissionOrHide>
                )}
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={3} style={{ marginBottom: 14 }}>
        <Grid size={6}>
          <Card sx={{ width: 1, minWidth: "100%" }}>
            <CardContent>
              <Stack
                spacing={2}
                justifyContent="space-between"
                minHeight={32}
              >
                {loading && (
                  <>
                    <Skeleton variant="text" sx={{ fontSize: "1.2rem" }} width={240} />
                    <Skeleton
                      variant="text"
                      sx={{ fontSize: "0.5em", mb: 0.5 }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{ fontSize: "0.5em" }}
                      width={420}
                    />
                  </>
                )}

                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    padding: '10px 0',
                    borderRadius: '4px',
                  }}
                >
                  {!loading && !pastVisits?.length && (
                    <Typography component="span">
                      No past visits.
                    </Typography>
                  )}
                  {pastVisits?.length ? (
                    <VisitTimeline
                      visits={pastVisits}
                      onEdit={setVisitIdToBeEdited}
                      onCancel={setVisitIdToBeCanceled}
                      activeVisitId={visitPopup.visitId}
                      activeVisitAnchor={visitPopup.anchorEl}
                      onOpenVisit={(visitId, anchorEl) => {
                        setVisitPopup({ visitId, anchorEl });
                      }}
                      onCloseVisit={handleCloseVisit}
                    />
                  ) : null}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={6}>
          <Card sx={{ width: 1, minWidth: "100%", marginBottom: 2 }}>
            <CardContent>
              <Stack
                spacing={2}
                justifyContent="space-between"
                minHeight={32}
              >
                {loading && (
                  <>
                    <Skeleton variant="text" sx={{ fontSize: "1.2rem" }} width={240} />
                    <Skeleton
                      variant="text"
                      sx={{ fontSize: "0.5em", mb: 0.5 }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{ fontSize: "0.5em" }}
                      width={420}
                    />
                  </>
                )}

                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    padding: '10px 0',
                    borderRadius: '4px',
                  }}
                >
                  {!loading && !upcomingVisits?.length && (
                    <Typography component="span">
                      No upcoming visits.
                    </Typography>
                  )}
                  {upcomingVisits?.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())?.map((visit) => (
                    <VisitCard
                      key={visit.id}
                      visit={visit}
                      onEdit={setVisitIdToBeEdited}
                      onCancel={setVisitIdToBeCanceled}
                      isDetailsOpen={visitPopup.visitId === visit.id}
                      detailsAnchor={visitPopup.visitId === visit.id ? visitPopup.anchorEl : null}
                      onOpenDetails={(anchorEl) => {
                        setVisitPopup({ visitId: visit.id, anchorEl });
                      }}
                      onCloseDetails={handleCloseVisit}
                    />
                  ))}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PatientCareTeamList
        continuityProviders={continuityProviders}
        loading={loading}
        patientId={patient?.id || 0}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : ''}
      />

      {/* Admission Coordination Dialog */}
      <AdmissionCoordinationDialog
        open={admissionCoordination.isOpen}
        onClose={admissionCoordination.closeDialog}
        onComplete={admissionCoordination.handleComplete}
        isSimulationMode={false}
        admissionVisitType={admissionCoordination.admissionVisitType}
      />
    </Container>
  );
}



export default PatientDetailScreen;
